import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  List,
  ListItem,
  Avatar,
  Stack,
  Paper,
} from "@mui/material";
import { QuestionAnswer, Send } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  useGetMessages,
  useSendMessage,
} from "../../../services/stagemode_api/viewers/viewers_api";
import { useAuth } from "../../../hooks";
import SocketHandler from "../../../utils/socketHandler";

const InteractionSection = styled(Paper)(() => ({
  padding: "24px",
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  color: "#FFFFFF",
  boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
  fontFamily: "Lexend, sans-serif",
  marginBottom: "24px",
}));

const QuestionInput = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "#111",
    color: "#fff",
    border: "1px solid #333",
    "& fieldset": {
      borderColor: "#444",
    },
    "&:hover fieldset": {
      borderColor: "#666",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#E50914",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    fontFamily: "Lexend, sans-serif",
    fontSize: "14px",
  },
  "& .MuiInputLabel-root": {
    color: "#9CA3AF",
  },
}));

const QuestionSection: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const { data: messagesData, refetch: refetchMessages } =
    useGetMessages(bookingId);
  const sendMessageMutation = useSendMessage(bookingId);
  const { userInfo } = useAuth();
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const socketSubscribedRef = useRef(false);

  useEffect(() => {
    // Initialize messages from API data
    if (messagesData?.data) {
      setMessages(messagesData.data);
    }
  }, [messagesData]);

  useEffect(() => {
    // Prevent multiple subscriptions
    if (socketSubscribedRef.current) {
      return;
    }

    // Get socket instance and subscribe to messages
    const socketHandler = SocketHandler.getInstance();

    socketHandler.subscribeToBooking(bookingId, (message) => {

      setMessages((prev) => [message, ...prev]);
      // Refetch messages to ensure consistency
      refetchMessages();
    });

    socketSubscribedRef.current = true;

    // Cleanup subscription when component unmounts
    return () => {
      socketHandler.unsubscribeFromBooking(bookingId);
      socketSubscribedRef.current = false;
    };
  }, [bookingId, refetchMessages]);

  const handleQuestionSubmit = async () => {
    try {
      await sendMessageMutation.mutateAsync({
        message: question,
        username: userInfo?.username ?? "",
      });
      setQuestion("");
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <InteractionSection>
      <Box display="flex" alignItems="center" mb={2}>
        <QuestionAnswer sx={{ mr: 1, color: "#E50914" }} />
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: "#FFFFFF",
            fontFamily: "Lexend",
          }}
        >
          Interact With Talent
        </Typography>
      </Box>

      <Box mb={3}>
        <QuestionInput
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          placeholder="Live chat With Talent"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
        <Box mt={2} textAlign="right">
          <Button
            variant="contained"
            endIcon={<Send />}
            onClick={handleQuestionSubmit}
            disabled={!question.trim()}
            sx={{
              fontFamily: "Lexend",
              fontWeight: 600,
              fontSize: "14px",
              px: 3,
              py: 1.25,
              borderRadius: "16px",
              textTransform: "none",
              backgroundColor: "#E50914",
              "&:hover": {
                backgroundColor: "#B20710",
              },
              "&:disabled": {
                backgroundColor: "#333",
                color: "#888",
              },
            }}
          >
            Submit
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 2, borderColor: "#333" }} />

      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: "Lexend",
          fontWeight: 600,
          fontSize: "15px",
          color: "#FFFFFF",
          mb: 2,
        }}
      >
        Recent Interaction
      </Typography>

      <List disablePadding sx={{ maxHeight: "300px", overflowY: "auto" }}>
        {messages.map((q: any) => (
          <ListItem
            key={q.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              mb: 2,
              px: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontFamily: "Lexend",
                color: "#F3F4F6",
                fontSize: "15px",
                fontWeight: 500,
                mb: 0.5,
              }}
            >
              {q?.message ?? ""}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar
                sx={{
                  width: 28,
                  height: 28,
                  fontSize: "13px",
                  bgcolor: "#E50914",
                }}
              >
                {q?.username?.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                variant="caption"
                sx={{
                  fontFamily: "Lexend",
                  color: "#9CA3AF",
                }}
              >
                {q.username} • {q?.likes ?? 0} likes
              </Typography>
            </Stack>
          </ListItem>
        ))}
      </List>
    </InteractionSection>
  );
};

export default QuestionSection;
